import { useEffect, useState } from 'react';
import { Box, Drawer, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import { IoClose } from 'react-icons/io5';
import Input from 'components/Common/Input';
import Button from 'components/Common/Button';
import { useForm } from 'react-hook-form';
import { useCheckHostSmsImportability } from '@/apis/business/setting/phoneNumbers/hook';
import { ADDON_DRAWER_WIDTH, getAddonDisplayTitle } from './addonDisplay';
import { BusinessAddon } from '@/apis/business/addons/types';

interface SmsEligibilityDrawerProps {
  addon: BusinessAddon | null;
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
}

interface FormValues {
  phone_number: string;
}

const apiErrorMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;

const toCheckNumber = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('1') && digits.length === 11) return `+${digits}`;
  return `+1${digits}`;
};

export default function SmsEligibilityDrawer({ addon, open, onClose, onContinue }: SmsEligibilityDrawerProps) {
  const [eligible, setEligible] = useState(false);
  const [checked, setChecked] = useState(false);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const { mutateAsync: checkImportability, isPending: checking } = useCheckHostSmsImportability();

  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: { phone_number: '' }
  });

  const phone = watch('phone_number');

  useEffect(() => {
    if (!open) return;
    setEligible(false);
    setChecked(false);
    setResultMessage(null);
    reset({ phone_number: '' });
  }, [open, reset]);

  if (!addon) return null;

  const onCheck = handleSubmit(async (values) => {
    setEligible(false);
    setChecked(false);
    setResultMessage(null);

    const number = toCheckNumber(values.phone_number);
    if (number.replace(/\D/g, '').length < 11) {
      setEligible(false);
      setChecked(true);
      setResultMessage('Enter a valid phone number with at least 10 digits to continue.');
      return;
    }

    try {
      await checkImportability({ host_sms: { number } });
      setEligible(true);
      setChecked(true);
      setResultMessage('This number is eligible for SMS hosting. You can continue.');
    } catch (error) {
      setEligible(false);
      setChecked(true);
      setResultMessage(apiErrorMessage(error, 'This number is not eligible for SMS hosting.'));
    }
  });

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: ADDON_DRAWER_WIDTH,
          maxWidth: '100%',
          p: 0,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2}
        borderBottom='1px solid #ECECEC'
      >
        <Typography variant='h6' fontWeight={600}>
          {getAddonDisplayTitle(addon)}
        </Typography>
        <IconButton onClick={onClose} size='small' aria-label='Close'>
          <IoClose size={22} />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={3} py={2.5}>
        <Typography fontSize={12} fontWeight={600} color='text.secondary' letterSpacing={0.4}>
          CHECK ELIGIBILITY
        </Typography>
        <Typography fontSize={14} mt={0.75} mb={2.5}>
          Check if your number is eligible for hosting.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'flex-start' }}>
          <Box flex={1} width='100%'>
            <Input
              name='phone_number'
              control={control}
              label='Phone Number'
              required
              fullWidth
              placeholder='(555) 123-4567'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Typography fontSize={14} color='text.secondary'>
                      +1
                    </Typography>
                  </InputAdornment>
                )
              }}
            />
          </Box>
          <Button
            variant='contained'
            onClick={onCheck}
            isPending={checking}
            disabled={!phone?.trim() || checking}
            sx={{ mt: { sm: 0.5 }, whiteSpace: 'nowrap', height: 40 }}
          >
            Check Eligibility
          </Button>
        </Stack>

        {checked && resultMessage && (
          <Typography mt={2} fontSize={13} color={eligible ? 'success.main' : 'error.main'}>
            {resultMessage}
          </Typography>
        )}
      </Box>

      <Stack direction='row' justifyContent='flex-end' gap={1.5} px={3} py={2} borderTop='1px solid #ECECEC'>
        <Button variant='outlined' color='inherit' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='contained' onClick={onContinue} disabled={!eligible}>
          Continue
        </Button>
      </Stack>
    </Drawer>
  );
}
